import React, { useState, useMemo, useEffect } from 'react';
import toast from 'react-hot-toast';
import { useAuth } from '../../context/AuthContext';

const API_BASE = 'https://api.glowytics.com';

const STEPS = ['discover', 'refine', 'launch'];
const STEP_LABELS = ['1. Discover Keywords', '2. Refine & Generate Copy', '3. Review & Launch'];

function ProgressBar({ step }) {
  const idx = STEPS.indexOf(step);
  const pct = ((idx + 1) / STEPS.length) * 100;
  return (
    <div className="mb-6">
      <div className="flex justify-between mb-1">
        {STEP_LABELS.map((label, i) => (
          <span
            key={i}
            className={`text-xs font-medium ${i <= idx ? 'text-blue-600' : 'text-gray-400'}`}
          >
            {label}
          </span>
        ))}
      </div>
      <div className="w-full bg-gray-200 rounded-full h-2">
        <div
          className="h-2 rounded-full transition-all duration-300 bg-blue-600"
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

function CompetitionBadge({ competition }) {
  if (!competition || competition === 'UNKNOWN' || competition === 'UNSPECIFIED') return null;
  const cls =
    competition === 'LOW'
      ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
      : competition === 'MEDIUM'
      ? 'bg-amber-50 text-amber-700 border-amber-200'
      : 'bg-red-50 text-red-700 border-red-200';
  return (
    <span className={`px-1.5 py-0.5 rounded text-[10px] border ${cls}`}>
      {competition}
    </span>
  );
}

export default function SearchCampaignWizard() {
  const { selectedBrand } = useAuth();

  // Step state
  const [step, setStep] = useState('discover');

  // Step 1
  const [seed, setSeed] = useState('');
  const [seedDomain, setSeedDomain] = useState('');
  const [discoveredKeywords, setDiscoveredKeywords] = useState([]);
  const [selectedKeywords, setSelectedKeywords] = useState([]);
  const [discovering, setDiscovering] = useState(false);
  const [discoverError, setDiscoverError] = useState(null);

  // Step 2
  const [brandName, setBrandName] = useState('');
  const [landingUrl, setLandingUrl] = useState('');
  const [rsaHeadlines, setRsaHeadlines] = useState([]);
  const [rsaDescriptions, setRsaDescriptions] = useState([]);
  const [generating, setGenerating] = useState(false);

  // Step 3
  const today = useMemo(() => new Date().toISOString().slice(0, 10), []);
  const [campaignName, setCampaignName] = useState('');
  const [dailyBudget, setDailyBudget] = useState(100);
  const [bidStrategy, setBidStrategy] = useState('MAXIMIZE_CLICKS');
  const [targetCpa, setTargetCpa] = useState('');
  const [languageId, setLanguageId] = useState(1000);
  const [locationIds, setLocationIds] = useState([2792]);
  const [finalStatus, setFinalStatus] = useState('PAUSED');
  const [validating, setValidating] = useState(false);
  const [creating, setCreating] = useState(false);
  const [validatedOk, setValidatedOk] = useState(false);
  const [validationError, setValidationError] = useState(null);
  const [result, setResult] = useState(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  const brandId = useMemo(() => {
    try { return localStorage.getItem('currentBrandId') || selectedBrand?.id || ''; } catch { return ''; }
  }, [selectedBrand]);

  // Prefill brand name from selectedBrand
  useEffect(() => {
    if (selectedBrand?.name && !brandName) {
      setBrandName(selectedBrand.name);
    }
  }, [selectedBrand]); // eslint-disable-line react-hooks/exhaustive-deps

  // Set default campaign name when entering step 2
  useEffect(() => {
    if (step === 'refine' && !campaignName) {
      setCampaignName(`[GLW] ${seed || 'Search'} - ${today}`);
    }
  }, [step]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reset validation when key fields change
  useEffect(() => {
    setValidatedOk(false);
    setValidationError(null);
  }, [campaignName, dailyBudget, bidStrategy, targetCpa, languageId, locationIds, finalStatus, rsaHeadlines, rsaDescriptions, selectedKeywords]);

  // Role guard
  const role = selectedBrand?.currentUserRole;
  if (role && !['OWNER', 'ADMIN'].includes(role)) {
    return (
      <div className="p-6 max-w-2xl mx-auto">
        <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
          <h2 className="text-lg font-semibold text-red-700 mb-2">Access Denied</h2>
          <p className="text-sm text-red-600">
            You need OWNER or ADMIN role to access the Search Campaign Wizard.
          </p>
        </div>
      </div>
    );
  }

  // ── Step 1 helpers ─────────────────────────────────────────────────────────

  const handleDiscover = async () => {
    if (!seed.trim()) {
      toast.error('Enter a seed keyword first.');
      return;
    }
    setDiscovering(true);
    setDiscoverError(null);
    setDiscoveredKeywords([]);
    try {
      const res = await fetch(`${API_BASE}/api/keyword-discover/suggestions`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', 'X-Brand-ID': brandId },
        body: JSON.stringify({
          seedKeywords: [seed.trim()],
          pageUrl: seedDomain.trim() || undefined,
          languageConstant: '1000',
          geoTargetConstant: 'geoTargetConstants/2792',
        }),
      });
      const ct = res.headers.get('content-type') || '';
      if (!res.ok) {
        let msg = 'Discovery failed';
        if (ct.includes('application/json')) {
          const err = await res.json().catch(() => ({}));
          msg = err.error || msg;
        }
        throw new Error(msg);
      }
      const data = await res.json();
      const list = Array.isArray(data?.ideas) ? data.ideas : [];
      setDiscoveredKeywords(list.slice(0, 75));
      if (list.length === 0) toast('No keywords found. Try a different seed.');
    } catch (e) {
      setDiscoverError(e.message || 'Failed to fetch suggestions');
      toast.error(e.message || 'Discovery failed');
    } finally {
      setDiscovering(false);
    }
  };

  const toggleKeyword = (kw) => {
    setSelectedKeywords((prev) => {
      const exists = prev.find((k) => k.text === kw.text);
      if (exists) return prev.filter((k) => k.text !== kw.text);
      return [...prev, { ...kw, matchType: 'PHRASE' }];
    });
  };

  const removeKeyword = (text) => {
    setSelectedKeywords((prev) => prev.filter((k) => k.text !== text));
  };

  const isKwSelected = (text) => selectedKeywords.some((k) => k.text === text);

  // ── Step 2 helpers ─────────────────────────────────────────────────────────

  const handleGenerateRsa = async () => {
    if (!brandName.trim() || !landingUrl.trim()) {
      toast.error('Brand name and landing URL are required before generating copy.');
      return;
    }
    setGenerating(true);
    setRsaHeadlines([]);
    setRsaDescriptions([]);
    try {
      const res = await fetch(`${API_BASE}/api/labs/google-ads/wizard/generate-rsa`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', 'X-Brand-ID': brandId },
        body: JSON.stringify({
          brandName: brandName.trim(),
          landingUrl: landingUrl.trim(),
          keywords: selectedKeywords.map((k) => k.text),
        }),
      });
      const ct = res.headers.get('content-type') || '';
      if (!res.ok) {
        let msg = 'Ad copy generation failed';
        if (ct.includes('application/json')) {
          const err = await res.json().catch(() => ({}));
          msg = err.error || msg;
        }
        throw new Error(msg);
      }
      const data = await res.json();
      setRsaHeadlines(Array.isArray(data.headlines) ? data.headlines.slice(0, 15) : []);
      setRsaDescriptions(Array.isArray(data.descriptions) ? data.descriptions.slice(0, 4) : []);
      toast.success('Ad copy generated!');
    } catch (e) {
      toast.error(e.message || 'Failed to generate ad copy');
    } finally {
      setGenerating(false);
    }
  };

  const updateHeadline = (idx, val) => {
    setRsaHeadlines((prev) => { const n = [...prev]; n[idx] = val; return n; });
  };

  const updateDescription = (idx, val) => {
    setRsaDescriptions((prev) => { const n = [...prev]; n[idx] = val; return n; });
  };

  const validHeadlines = rsaHeadlines.filter((h) => h && h.length <= 30);
  const validDescriptions = rsaDescriptions.filter((d) => d && d.length <= 90);

  const step2Valid =
    validHeadlines.length >= 3 &&
    validDescriptions.length >= 2 &&
    brandName.trim() !== '' &&
    landingUrl.trim() !== '';

  // ── Step 3 helpers ─────────────────────────────────────────────────────────

  const buildPayload = (validateOnly) => ({
    validateOnly,
    brandName: brandName.trim(),
    landingUrl: landingUrl.trim(),
    campaignName: campaignName.trim(),
    dailyBudgetMicros: Math.round(dailyBudget * 1_000_000),
    bidStrategy,
    targetCpa: bidStrategy === 'MAXIMIZE_CONVERSIONS' && targetCpa ? Number(targetCpa) : undefined,
    languageId,
    locationIds,
    finalStatus,
    headlines: rsaHeadlines.filter((h) => h && h.length <= 30),
    descriptions: rsaDescriptions.filter((d) => d && d.length <= 90),
    keywords: selectedKeywords.map((k) => ({ text: k.text, matchType: k.matchType || 'PHRASE' })),
  });

  const handleValidate = async () => {
    setValidating(true);
    setValidatedOk(false);
    setValidationError(null);
    try {
      const res = await fetch(`${API_BASE}/api/labs/google-ads/wizard/create-campaign`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', 'X-Brand-ID': brandId },
        body: JSON.stringify(buildPayload(true)),
      });
      const ct = res.headers.get('content-type') || '';
      if (!res.ok) {
        let msg = 'Validation failed';
        let errors = null;
        if (ct.includes('application/json')) {
          const err = await res.json().catch(() => ({}));
          msg = err.error || msg;
          errors = err.errors || err.details || null;
        }
        setValidationError({ message: msg, errors });
        toast.error(msg);
        return;
      }
      setValidatedOk(true);
      toast.success('Validation passed! Ready to create campaign.');
    } catch (e) {
      setValidationError({ message: e.message || 'Validation error' });
      toast.error(e.message || 'Validation failed');
    } finally {
      setValidating(false);
    }
  };

  const handleCreate = async () => {
    setShowConfirmDialog(false);
    setCreating(true);
    try {
      const res = await fetch(`${API_BASE}/api/labs/google-ads/wizard/create-campaign`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', 'X-Brand-ID': brandId },
        body: JSON.stringify(buildPayload(false)),
      });
      const ct = res.headers.get('content-type') || '';
      if (!res.ok) {
        let msg = 'Campaign creation failed';
        if (ct.includes('application/json')) {
          const err = await res.json().catch(() => ({}));
          msg = err.error || msg;
        }
        throw new Error(msg);
      }
      const data = await res.json();
      setResult(data);
      toast.success('Campaign created successfully!');
    } catch (e) {
      toast.error(e.message || 'Campaign creation failed');
    } finally {
      setCreating(false);
    }
  };

  const handleReset = () => {
    setStep('discover');
    setSeed('');
    setSeedDomain('');
    setDiscoveredKeywords([]);
    setSelectedKeywords([]);
    setDiscoverError(null);
    setBrandName(selectedBrand?.name || '');
    setLandingUrl('');
    setRsaHeadlines([]);
    setRsaDescriptions([]);
    setCampaignName('');
    setDailyBudget(100);
    setBidStrategy('MAXIMIZE_CLICKS');
    setTargetCpa('');
    setLanguageId(1000);
    setLocationIds([2792]);
    setFinalStatus('PAUSED');
    setValidatedOk(false);
    setValidationError(null);
    setResult(null);
  };

  // ── Success View ────────────────────────────────────────────────────────────

  if (result) {
    const campaignUrl =
      result.campaign_id && result.customer_id
        ? `https://ads.google.com/aw/campaigns?campaignId=${result.campaign_id}&__c=${result.customer_id}`
        : null;
    return (
      <div className="p-6 max-w-2xl mx-auto">
        <div className="bg-white rounded-xl border shadow-sm p-8 text-center">
          <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">Campaign Created!</h2>
          {result.campaign_id && (
            <p className="text-sm text-gray-600 mb-1">
              Campaign ID: <span className="font-mono font-medium">{result.campaign_id}</span>
            </p>
          )}
          {result.customer_id && (
            <p className="text-sm text-gray-600 mb-4">
              Account: <span className="font-mono">{result.customer_id}</span>
            </p>
          )}
          {campaignUrl && (
            <a
              href={campaignUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 mb-4"
            >
              View in Google Ads
            </a>
          )}
          <div>
            <button
              onClick={handleReset}
              className="px-4 py-2 bg-gray-100 text-gray-700 text-sm rounded-lg hover:bg-gray-200"
            >
              Create Another Campaign
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── Main Wizard ─────────────────────────────────────────────────────────────

  return (
    <div className="p-6 max-w-5xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-2 mb-1">
          <span className="px-2 py-0.5 rounded text-[10px] font-semibold bg-amber-100 text-amber-700 border border-amber-200">LAB</span>
          <h2 className="text-xl font-bold text-gray-900">Search Campaign Wizard</h2>
        </div>
        <p className="text-sm text-gray-500">
          Create a Google Ads Search campaign in 3 steps: discover keywords, refine &amp; generate AI copy, then launch.
        </p>
      </div>

      <ProgressBar step={step} />

      {/* ── STEP 1: DISCOVER ─────────────────────────────────────────────────── */}
      {step === 'discover' && (
        <div className="space-y-5">
          {/* Inputs */}
          <div className="bg-white rounded-xl border shadow-sm p-5">
            <h3 className="text-base font-semibold text-gray-800 mb-4">Step 1: Discover Keywords</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Seed keyword or topic <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={seed}
                  onChange={(e) => setSeed(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleDiscover(); } }}
                  placeholder="e.g. running shoes"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Brand domain <span className="text-gray-400 font-normal">(optional)</span>
                </label>
                <input
                  type="text"
                  value={seedDomain}
                  onChange={(e) => setSeedDomain(e.target.value)}
                  placeholder="e.g. https://example.com"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
            <button
              onClick={handleDiscover}
              disabled={discovering || !seed.trim()}
              className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {discovering ? 'Discovering...' : 'Discover Keywords'}
            </button>
          </div>

          {/* Selected keywords pills */}
          {selectedKeywords.length > 0 && (
            <div className="bg-blue-50 border border-blue-100 rounded-xl p-4">
              <p className="text-xs font-medium text-blue-700 mb-2">
                Selected ({selectedKeywords.length}) — need at least 5 to continue
              </p>
              <div className="flex flex-wrap gap-2">
                {selectedKeywords.map((kw) => (
                  <span
                    key={kw.text}
                    className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs rounded-full bg-blue-600 text-white"
                  >
                    {kw.text}
                    <button
                      onClick={() => removeKeyword(kw.text)}
                      className="hover:opacity-70 leading-none"
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Results grid */}
          <div className="bg-white rounded-xl border shadow-sm p-5">
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-sm font-medium text-gray-800">
                Keyword Ideas {discoveredKeywords.length > 0 && `(${discoveredKeywords.length})`}
              </h4>
              {discoveredKeywords.length > 0 && (
                <span className="text-xs text-gray-500">Click a keyword to select it</span>
              )}
            </div>

            {discovering ? (
              <div className="space-y-2">
                {[...Array(8)].map((_, i) => (
                  <div key={i} className="h-10 bg-gray-100 rounded animate-pulse" />
                ))}
              </div>
            ) : discoverError ? (
              <div className="text-sm text-red-600 py-4">{discoverError}</div>
            ) : discoveredKeywords.length === 0 ? (
              <div className="text-sm text-gray-400 py-8 text-center">
                Enter a seed keyword above and click Discover.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full text-xs">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-2 text-left font-semibold text-gray-600">Keyword</th>
                      <th className="px-3 py-2 text-right font-semibold text-gray-600">Avg Monthly Searches</th>
                      <th className="px-3 py-2 text-center font-semibold text-gray-600">Competition</th>
                      <th className="px-3 py-2 text-right font-semibold text-gray-600">Bid Low</th>
                      <th className="px-3 py-2 text-right font-semibold text-gray-600">Bid High</th>
                    </tr>
                  </thead>
                  <tbody>
                    {discoveredKeywords.map((kw, i) => {
                      const selected = isKwSelected(kw.text);
                      return (
                        <tr
                          key={i}
                          onClick={() => toggleKeyword(kw)}
                          className={`cursor-pointer border-t ${
                            selected
                              ? 'bg-blue-50 hover:bg-blue-100'
                              : 'hover:bg-gray-50'
                          }`}
                        >
                          <td className="px-3 py-2 font-medium text-gray-900">
                            <div className="flex items-center gap-2">
                              {selected && (
                                <span className="w-3 h-3 rounded-full bg-blue-500 flex-shrink-0" />
                              )}
                              {kw.text}
                            </div>
                          </td>
                          <td className="px-3 py-2 text-right font-mono text-gray-700">
                            {kw.avgMonthlySearches?.toLocaleString() ?? '-'}
                          </td>
                          <td className="px-3 py-2 text-center">
                            <CompetitionBadge competition={kw.competition} />
                          </td>
                          <td className="px-3 py-2 text-right font-mono text-gray-600">
                            {kw.top_of_page_bid_low != null ? `${kw.top_of_page_bid_low.toFixed(2)} ₺` : '-'}
                          </td>
                          <td className="px-3 py-2 text-right font-mono text-gray-600">
                            {kw.top_of_page_bid_high != null ? `${kw.top_of_page_bid_high.toFixed(2)} ₺` : '-'}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex justify-end">
            <button
              onClick={() => setStep('refine')}
              disabled={selectedKeywords.length < 5}
              className="px-5 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Next: Refine &amp; Generate Copy →
            </button>
          </div>
          {selectedKeywords.length > 0 && selectedKeywords.length < 5 && (
            <p className="text-xs text-amber-600 text-right">
              Select {5 - selectedKeywords.length} more keyword{5 - selectedKeywords.length !== 1 ? 's' : ''} to continue.
            </p>
          )}
        </div>
      )}

      {/* ── STEP 2: REFINE ──────────────────────────────────────────────────── */}
      {step === 'refine' && (
        <div className="space-y-5">
          {/* Keywords table */}
          <div className="bg-white rounded-xl border shadow-sm p-5">
            <h3 className="text-base font-semibold text-gray-800 mb-4">Step 2: Refine Keywords &amp; Generate Ad Copy</h3>
            <div className="overflow-x-auto mb-4">
              <table className="min-w-full text-xs">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-left font-semibold text-gray-600">Keyword</th>
                    <th className="px-3 py-2 text-center font-semibold text-gray-600">Match Type</th>
                    <th className="px-3 py-2 text-right font-semibold text-gray-600">Avg Volume</th>
                    <th className="px-3 py-2 text-center font-semibold text-gray-600">Remove</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedKeywords.map((kw, i) => (
                    <tr key={i} className="border-t hover:bg-gray-50">
                      <td className="px-3 py-2 font-medium text-gray-900">{kw.text}</td>
                      <td className="px-3 py-2 text-center">
                        <select
                          value={kw.matchType || 'PHRASE'}
                          onChange={(e) => {
                            setSelectedKeywords((prev) => {
                              const n = [...prev];
                              n[i] = { ...n[i], matchType: e.target.value };
                              return n;
                            });
                          }}
                          className="text-xs border border-gray-300 rounded px-2 py-1 bg-white"
                        >
                          <option value="BROAD">BROAD</option>
                          <option value="PHRASE">PHRASE</option>
                          <option value="EXACT">EXACT</option>
                        </select>
                      </td>
                      <td className="px-3 py-2 text-right font-mono text-gray-700">
                        {kw.avgMonthlySearches?.toLocaleString() ?? '-'}
                      </td>
                      <td className="px-3 py-2 text-center">
                        <button
                          onClick={() => removeKeyword(kw.text)}
                          className="text-red-400 hover:text-red-600 text-base leading-none"
                        >
                          ×
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Brand + URL */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-5">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Brand Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={brandName}
                  onChange={(e) => setBrandName(e.target.value)}
                  placeholder="e.g. Acme Co"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Landing URL <span className="text-red-500">*</span>
                </label>
                <input
                  type="url"
                  value={landingUrl}
                  onChange={(e) => setLandingUrl(e.target.value)}
                  placeholder="https://example.com/landing"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            {/* Generate button */}
            <div className="flex items-center gap-3">
              <button
                onClick={handleGenerateRsa}
                disabled={generating || !brandName.trim() || !landingUrl.trim()}
                className="px-4 py-2 bg-purple-600 text-white text-sm rounded-lg hover:bg-purple-700 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {generating ? 'Generating...' : 'Generate Ad Copy with AI'}
              </button>
              {rsaHeadlines.length > 0 && (
                <button
                  onClick={handleGenerateRsa}
                  disabled={generating}
                  className="px-3 py-2 text-sm text-purple-600 border border-purple-200 rounded-lg hover:bg-purple-50 disabled:opacity-40"
                >
                  Regenerate
                </button>
              )}
            </div>
          </div>

          {/* Ad copy editor */}
          {(generating || rsaHeadlines.length > 0) && (
            <div className="bg-white rounded-xl border shadow-sm p-5">
              <h4 className="text-sm font-semibold text-gray-800 mb-3">
                Ad Copy{' '}
                <span className="text-xs font-normal text-gray-500">
                  ({validHeadlines.length}/15 valid headlines, {validDescriptions.length}/4 valid descriptions)
                </span>
              </h4>

              {generating ? (
                <div className="space-y-2">
                  {[...Array(6)].map((_, i) => (
                    <div key={i} className="h-8 bg-gray-100 rounded animate-pulse" />
                  ))}
                </div>
              ) : (
                <>
                  <p className="text-xs font-medium text-gray-600 mb-2">Headlines (max 30 chars each)</p>
                  <div className="space-y-1.5 mb-4">
                    {rsaHeadlines.map((h, i) => {
                      const over = h.length > 30;
                      return (
                        <div key={i} className="flex items-center gap-2">
                          <input
                            type="text"
                            value={h}
                            onChange={(e) => updateHeadline(i, e.target.value)}
                            className={`flex-1 px-2 py-1 text-xs border rounded focus:outline-none focus:ring-1 ${
                              over ? 'border-red-400 focus:ring-red-300' : 'border-gray-300 focus:ring-blue-300'
                            }`}
                          />
                          <span className={`text-[10px] w-8 text-right ${over ? 'text-red-500' : 'text-gray-400'}`}>
                            {h.length}/30
                          </span>
                        </div>
                      );
                    })}
                  </div>

                  <p className="text-xs font-medium text-gray-600 mb-2">Descriptions (max 90 chars each)</p>
                  <div className="space-y-1.5">
                    {rsaDescriptions.map((d, i) => {
                      const over = d.length > 90;
                      return (
                        <div key={i} className="flex items-center gap-2">
                          <input
                            type="text"
                            value={d}
                            onChange={(e) => updateDescription(i, e.target.value)}
                            className={`flex-1 px-2 py-1 text-xs border rounded focus:outline-none focus:ring-1 ${
                              over ? 'border-red-400 focus:ring-red-300' : 'border-gray-300 focus:ring-blue-300'
                            }`}
                          />
                          <span className={`text-[10px] w-8 text-right ${over ? 'text-red-500' : 'text-gray-400'}`}>
                            {d.length}/90
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
            </div>
          )}

          {/* Footer */}
          <div className="flex justify-between">
            <button
              onClick={() => setStep('discover')}
              className="px-4 py-2 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              ← Back
            </button>
            <button
              onClick={() => { if (!campaignName) setCampaignName(`[GLW] ${seed || 'Search'} - ${today}`); setStep('launch'); }}
              disabled={!step2Valid}
              className="px-5 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Next: Review &amp; Launch →
            </button>
          </div>
          {rsaHeadlines.length > 0 && !step2Valid && (
            <p className="text-xs text-amber-600 text-right">
              Need at least 3 valid headlines and 2 valid descriptions to continue.
            </p>
          )}
        </div>
      )}

      {/* ── STEP 3: LAUNCH ──────────────────────────────────────────────────── */}
      {step === 'launch' && (
        <div className="space-y-5">
          {/* Summary */}
          <div className="bg-white rounded-xl border shadow-sm p-5">
            <h3 className="text-base font-semibold text-gray-800 mb-4">Step 3: Review &amp; Launch</h3>

            {/* Ad preview */}
            {rsaHeadlines.length > 0 && (
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-5">
                <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-2">Ad Preview</p>
                <p className="text-sm font-medium text-blue-600 truncate">{rsaHeadlines[0]}</p>
                <p className="text-xs text-green-700 mb-1">{landingUrl}</p>
                <p className="text-xs text-gray-600">{rsaDescriptions[0]}</p>
              </div>
            )}

            {/* Read-only summary */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-5 text-sm">
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-xs text-gray-500 mb-0.5">Brand</p>
                <p className="font-medium text-gray-900">{brandName}</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-xs text-gray-500 mb-0.5">Keywords</p>
                <p className="font-medium text-gray-900">
                  {selectedKeywords.length} keywords — {selectedKeywords.slice(0, 3).map(k => k.text).join(', ')}
                  {selectedKeywords.length > 3 ? `... +${selectedKeywords.length - 3} more` : ''}
                </p>
              </div>
            </div>

            {/* Editable campaign settings */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Campaign Name</label>
                <input
                  type="text"
                  value={campaignName}
                  onChange={(e) => setCampaignName(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Daily Budget (₺)</label>
                <input
                  type="number"
                  value={dailyBudget}
                  onChange={(e) => setDailyBudget(Number(e.target.value))}
                  min={10}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Bid Strategy</label>
                <select
                  value={bidStrategy}
                  onChange={(e) => setBidStrategy(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="MAXIMIZE_CLICKS">Maximize Clicks</option>
                  <option value="MAXIMIZE_CONVERSIONS">Maximize Conversions</option>
                  <option value="MANUAL_CPC">Manual CPC</option>
                </select>
              </div>
              {bidStrategy === 'MAXIMIZE_CONVERSIONS' && (
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Target CPA (₺)</label>
                  <input
                    type="number"
                    value={targetCpa}
                    onChange={(e) => setTargetCpa(e.target.value)}
                    placeholder="e.g. 50"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              )}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Language</label>
                <select
                  value={languageId}
                  onChange={(e) => setLanguageId(Number(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value={1055}>Turkish</option>
                  <option value={1000}>English</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Location</label>
                <select
                  value={locationIds[0] || 2792}
                  onChange={(e) => setLocationIds([Number(e.target.value)])}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value={2792}>Turkey</option>
                  <option value={2840}>United States</option>
                </select>
              </div>
            </div>

            {/* Status toggle */}
            <div className="mt-4">
              <label className="block text-xs font-medium text-gray-700 mb-2">Campaign Status</label>
              <div className="flex gap-3">
                <button
                  onClick={() => setFinalStatus('PAUSED')}
                  className={`flex-1 py-2 text-sm rounded-lg border transition-colors ${
                    finalStatus === 'PAUSED'
                      ? 'bg-blue-600 border-blue-600 text-white'
                      : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  PAUSED (recommended)
                </button>
                <button
                  onClick={() => setFinalStatus('ENABLED')}
                  className={`flex-1 py-2 text-sm rounded-lg border transition-colors ${
                    finalStatus === 'ENABLED'
                      ? 'bg-amber-500 border-amber-500 text-white'
                      : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  ENABLED
                </button>
              </div>
              {finalStatus === 'ENABLED' && (
                <div className="mt-2 flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg p-3">
                  <span className="text-amber-600 text-base leading-none mt-0.5">!</span>
                  <p className="text-xs text-amber-700">
                    Campaign will start serving immediately and spend budget.
                  </p>
                </div>
              )}
            </div>

            {/* Validation status */}
            {validatedOk && (
              <div className="mt-4 flex items-center gap-2 bg-emerald-50 border border-emerald-200 rounded-lg p-3">
                <svg className="w-4 h-4 text-emerald-600 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <p className="text-xs text-emerald-700 font-medium">Validation passed. Ready to create campaign.</p>
              </div>
            )}
            {validationError && (
              <div className="mt-4 bg-red-50 border border-red-200 rounded-lg p-3">
                <p className="text-xs font-medium text-red-700 mb-1">Validation failed: {validationError.message}</p>
                {validationError.errors && (
                  <ul className="list-disc list-inside text-xs text-red-600 space-y-0.5">
                    {(Array.isArray(validationError.errors)
                      ? validationError.errors
                      : [String(validationError.errors)]
                    ).map((e, i) => (
                      <li key={i}>{String(e)}</li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>

          {/* Footer - sticky-ish */}
          <div className="flex items-center justify-between gap-3 bg-white border rounded-xl shadow-sm p-4">
            <button
              onClick={() => setStep('refine')}
              className="px-4 py-2 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              ← Back
            </button>
            <div className="flex items-center gap-3">
              <button
                onClick={handleValidate}
                disabled={validating || creating}
                className="px-4 py-2 text-sm border border-blue-400 text-blue-600 rounded-lg hover:bg-blue-50 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {validating ? 'Validating...' : 'Validate'}
              </button>
              <button
                onClick={() => setShowConfirmDialog(true)}
                disabled={!validatedOk || creating || validating}
                className="px-5 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed font-medium"
              >
                {creating ? 'Creating...' : 'Create Campaign'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Confirmation Dialog ─────────────────────────────────────────────── */}
      {showConfirmDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl shadow-xl p-6 max-w-md w-full mx-4">
            <h3 className="text-base font-semibold text-gray-900 mb-2">Create Campaign?</h3>
            <p className="text-sm text-gray-600 mb-4">
              This will create a real Google Ads campaign for{' '}
              <strong>{brandName}</strong> with status{' '}
              <strong>{finalStatus}</strong>.{' '}
              {finalStatus === 'ENABLED' && (
                <span className="text-amber-600">The campaign will start spending immediately.</span>
              )}
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowConfirmDialog(false)}
                className="px-4 py-2 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleCreate}
                className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium"
              >
                Confirm &amp; Create
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
